/**
 * Which entity a `wayai use` / `wayai unbind` selector names.
 *
 * The worktree scope spans every subtree from one command, so the selector has
 * to say WHICH axis it means. It uses the subtree router's conventions rather than a
 * private grammar — a `--hub`/`--base`/`--community` flag, a `hubs/<name>` /
 * `bases/<name>` / `communities/<name>` prefix, a real path, or a bare name
 * decided by which of `hubs/` and `bases/` holds a folder of that name — so a
 * user who has learned `wayai push bases/crm` already knows how to spell
 * `wayai use bases/crm`. A community is never named by a bare name, as the
 * router never resolves one.
 *
 * What it does NOT inherit is `routeSubtree`'s rules 3–5 (the enclosing folder,
 * the repo default, the sole candidate). Those exist so a BARE `pull`/`push`
 * finds a target; a scope command that inherited them would bind a worktree to
 * an entity the user never named. The scope is a guard, never a selector, so
 * every scope change is explicit.
 *
 * A name present in BOTH subtrees is refused, never silently picked — the subtree
 * router's refusal, and the same refusal TEXT, because what the user needs is the two
 * qualified invocations that replace the ambiguous one.
 */

import * as path from 'node:path';
import { expected } from './expected.js';
import { assertValidBaseSelector, resolveBaseSelectorToId } from './base-workspace.js';
import { resolveCommunitySelectorToId } from './community-workspace.js';
import { resolveLayout, hubsDirLabel } from './layout.js';
import {
  hubAndBaseRefusal,
  mixedInvocationRefusal,
  parseRoutingTokens,
  resolveSelectorSubtree,
  subtreeFlags,
} from './subtree-routing.js';
import { UUID_RE } from './utils.js';
import {
  detectWorkspace,
  findGitRoot,
  findHubByFolderName,
  scanNewHubs,
  scanWorkspaceHubs,
} from './workspace.js';
import type { ScopeAxis } from './worktree-scope.js';

/** One resolved scope target: the axis it belongs to and the id stored in the scope. */
export interface ScopeTarget {
  axis: ScopeAxis;
  id: string;
}

/** The axis and the RAW selector token, before it is resolved to an id. */
export interface ScopeSelector {
  axis: ScopeAxis;
  /** The selector with any subtree prefix or path stripped — one bare name. */
  target: string;
  /**
   * The repo root as resolved while routing this selector, so the id resolution
   * below does not spawn `git rev-parse` again. `findGitRoot` shells out on every
   * call and caches nothing; resolving a bare hub folder name used to cost three
   * spawns on top of the store's own.
   */
  gitRoot: string | null;
}

/**
 * The `--add` token as it was actually typed, or undefined.
 *
 * Recognition only — this function never decides what to DO about it, because
 * the two commands want opposite things: `use` honours a bare `--add` and
 * refuses a valued one, `unbind` refuses every spelling. Deciding here forced one
 * verb's recovery advice onto the other, which is how `wayai unbind --add=false`
 * came to print two `wayai use` commands that both move the scope in the opposite
 * direction from the removal the user asked for.
 *
 * Recognition still has to be SHARED, though: `unbind` can only refuse a flag it
 * recognises, and an unrecognised `--add` there is skipped by
 * `parseRoutingTokens` as an ordinary flag token and the entry is silently
 * removed anyway.
 */
export function findAddFlag(args: string[]): string | undefined {
  return args.find((arg) => arg === '--add' || arg.startsWith('--add='));
}

/**
 * True when `token` carries a value (`--add=false`) rather than being bare.
 *
 * `--add` is a boolean: it selects between widening an axis and replacing it, and
 * those are opposite writes. Reading `--add=false` as "set" WIDENS — the
 * previously scoped entity stays in the set and the tripwire keeps admitting it,
 * which is the wrong-worktree admission the scope exists to refuse. Reading it as
 * absent silently REPLACES instead. Neither reading is defensible, so callers
 * refuse it and let the user restate the intent.
 *
 * Refusing also matches the sibling commander tree, whose boolean options reject
 * `--add=true` outright. The `=` habit that tree teaches is real, but it is a
 * habit for VALUE flags (`--base=crm`); this is not one.
 */
export function isValuedFlag(token: string): boolean {
  return token.includes('=');
}

/**
 * Which axis and which bare name a selector names, without touching the
 * workspace beyond the folder-existence test the subtree router already performs.
 *
 * Throws an expected error for the two refusals: more than one subtree flag at
 * once, and a bare name that exists in both `hubs/` and `bases/`.
 */
export function resolveScopeSelector(verb: string, args: string[]): ScopeSelector | null {
  const { hubFlag, baseFlag, communityFlag, positional } = parseRoutingTokens(args);

  const flagged = subtreeFlags({ hubFlag, baseFlag, communityFlag });
  if (flagged.length > 1) {
    throw expected(mixedInvocationRefusal(verb, flagged));
  }
  // Three ways a subtree flag arrives without a usable target. They fail
  // DIFFERENTLY, which is why the guards below take both what
  // `parseRoutingTokens` recorded and the raw args:
  //
  //  - value is the NEXT FLAG (`wayai use --base --add`) — recorded as `'--add'`,
  //    a structurally valid base id, so it would be written to the scope;
  //  - NO value at all (`wayai unbind --base`, the flag last) — the token is
  //    consumed by the value-flag branch and NOTHING is recorded, so the whole
  //    invocation reads as bare. On `unbind` the bare reading is the destructive
  //    one, which is what made this the serious case;
  //  - an EMPTY value (`--base=`) — recorded as `''`, not as nothing, so a
  //    selector always came back and the destructive branch was never reached.
  //    It resolved to an empty base id and was refused downstream by
  //    `assertValidBaseSelector` with `Invalid base id ""` — accurate, but about
  //    a value the user never typed.
  //
  // `pull`/`push` survive all three only because their target resolution dies
  // later. The scope commands have no such second gate, so the flags are checked
  // here.
  // Two passes, and the order matters. In `--base --hub`, the `--hub` token is
  // CONSUMED as `--base`'s value, so both flags look faulty: `--base` has a
  // flag-shaped value, and `--hub` reads as typed-but-unrecorded. The first
  // description is the accurate one — the user typed `--hub` as a value, not as
  // a flag — so every bad-value case is reported before any missing-value case.
  assertValueIsNotAFlag(verb, '--hub', hubFlag);
  assertValueIsNotAFlag(verb, '--base', baseFlag);
  assertValueIsNotAFlag(verb, '--community', communityFlag);
  assertFlagWasGivenAValue(verb, '--hub', args, hubFlag);
  assertFlagWasGivenAValue(verb, '--base', args, baseFlag);
  assertFlagWasGivenAValue(verb, '--community', args, communityFlag);

  // A flag names its own subtree, so it never consults the workspace — that is
  // what makes `--base crm` reach a base with no folder in this checkout. The
  // repo root is still resolved, because the id resolution below needs it.
  const [flag] = flagged;
  if (flag) return { axis: flag.subtree, target: flag.target, gitRoot: findGitRoot() };
  if (positional === undefined) return null;

  const gitRoot = findGitRoot();
  const layout = gitRoot ? resolveLayout(gitRoot) : null;
  const resolved = resolveSelectorSubtree(layout, positional, process.cwd());

  if (resolved.kind === 'both') {
    throw expected(hubAndBaseRefusal(verb, resolved.target, resolved.target));
  }
  if (resolved.kind !== 'unknown') {
    return { axis: resolved.kind, target: resolved.target, gitRoot };
  }
  // A bare name naming no folder in either subtree. A UUID is a hub id and
  // nothing else, so it is unambiguous without a folder; any other bare name
  // goes to the hub side, whose resolver reports the miss in its own words and
  // lists the folders that would have worked. A base with no folder here is
  // reached by `--base` or `bases/<id>`, both of which decided above.
  return { axis: 'hubs', target: positional, gitRoot };
}

/** Refuse a subtree flag whose value is plainly the NEXT FLAG, not a target. */
function assertValueIsNotAFlag(verb: string, flag: string, captured: string | undefined): void {
  if (captured === undefined || !captured.startsWith('-')) return;
  throw refusal(verb, flag, `but got ${JSON.stringify(captured)} — which is a flag, not a hub, base or community`);
}

/**
 * Refuse a subtree flag that was TYPED but carries no usable value.
 *
 * Two spellings, and the two conditions below are both load-bearing because they
 * fail differently:
 *
 *  - `captured === undefined` — `wayai unbind --base` with the flag last.
 *    `parseRoutingTokens` consumes the token and records NOTHING, so the whole
 *    invocation reads as bare; on `unbind` that is the branch that clears the
 *    entire scope. This is the destructive one.
 *  - `captured === ''` — `--base=`. Recorded as an empty string, NOT as nothing,
 *    so a selector always came back and the destructive branch was never
 *    reachable this way. It resolved to an empty base id and was refused
 *    downstream as `Invalid base id ""`; catching it here only replaces that
 *    with a message naming the flag the user actually typed.
 *
 * `args` is consulted rather than `captured` alone because in the first case the
 * two disagree — that is precisely what makes the flag invisible.
 */
function assertFlagWasGivenAValue(
  verb: string,
  flag: string,
  args: string[],
  captured: string | undefined,
): void {
  if (captured !== undefined && captured !== '') return;
  if (!args.some((arg) => arg === flag || arg.startsWith(`${flag}=`))) return;
  throw refusal(verb, flag, 'but no target followed it');
}

function refusal(verb: string, flag: string, problem: string): Error {
  return expected(
    `\`wayai ${verb} ${flag}\` needs a target, ${problem}.\n  wayai ${verb} ${flag} <${flag.slice(2)}>`,
  );
}

/**
 * A hub selector → the hub id the scope stores.
 *
 * A UUID is itself; a folder name is resolved through its `hub.yaml`. The two
 * misses keep the wording they had as `wayai use`'s own: an IDLESS folder is
 * redirected to `wayai create` (gh #3117) — it has no id to scope to, so the
 * generic miss left the user with nothing to do — and an unknown name lists the
 * folders that would have worked.
 *
 * Takes `gitRoot` for the same reason `resolveBaseSelectorToId` does: the caller
 * resolved it while routing, and re-deriving it here spawns `git` again.
 */
function resolveHubSelectorToId(gitRoot: string | null, selector: string): string {
  if (UUID_RE.test(selector)) return selector;

  const workspaceDir =
    detectWorkspace()?.workspaceDir ?? (gitRoot ? resolveLayout(gitRoot).hubsDir : null);
  if (!workspaceDir) {
    throw expected('Not inside a git repository, and no UUID provided.');
  }

  const match = findHubByFolderName(workspaceDir, selector);
  if (match) return match.hubId;

  // Both scans are hoisted here because the miss below needs both lists, and
  // each one re-reads the whole hubs dir and re-parses every `hub.yaml`.
  const newHubs = scanNewHubs(workspaceDir);
  const pending = newHubs.find((h) => path.basename(h.hubFolder) === selector);
  if (pending) {
    throw expected(
      `"${selector}" is a new hub that hasn't been created on the platform yet, so it has no id to bind to.\n` +
        `Create it first — the worktree scope picks it up automatically afterward:\n` +
        `  wayai create ${selector}`,
    );
  }

  throw expected(
    [
      `No hub matching "${selector}" found in ${hubsDirLabel(gitRoot)}/. Pass a UUID or a folder name from:`,
      ...scanWorkspaceHubs(workspaceDir).map((h) => `  ${path.basename(h.hubFolder)} (${h.hubId})`),
      ...newHubs.map(
        (h) =>
          `  ${path.basename(h.hubFolder)} (new — "${h.hubName}", run \`wayai create ${path.basename(h.hubFolder)}\`)`,
      ),
    ].join('\n'),
  );
}

/**
 * A resolved selector → the id the scope stores, per axis.
 *
 * The base side is deliberately total where the hub side is not: an unknown base
 * selector is a literal base id (the base need not have a folder here), while an
 * unknown hub selector is a miss, because a hub id is a UUID a user does not
 * type from memory. The base id is still validated, so a traversal selector is
 * refused before it can reach the scope file. A community selector is a UUID or
 * a community folder's name, as a hub's is (`resolveCommunitySelectorToId`).
 */
export function resolveScopeTarget(selector: ScopeSelector): ScopeTarget {
  const { axis, target, gitRoot } = selector;
  if (axis === 'hubs') {
    return { axis, id: resolveHubSelectorToId(gitRoot, target) };
  }
  if (axis === 'communities') {
    return { axis, id: resolveCommunitySelectorToId(gitRoot, target) };
  }
  const id = resolveBaseSelectorToId(gitRoot, target);
  // The scope store applies this same grammar on write; checked here too so the
  // refusal names the SELECTOR the user typed and states the rule, rather than
  // surfacing as the store's write error about a value they never saw. Shared
  // with the `pull`/`push` base resolver so one rule reads one way.
  assertValidBaseSelector(id, 'base id');
  return { axis, id };
}
